// TechniciansListTable.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import Hoot from "../../Hoot";
import { toast } from "react-toastify";
import ReactApexChart from "react-apexcharts";
import { Modal, Button, Tabs, Tab, Form, Spinner, Alert, Carousel } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import { FiEdit3 } from "react-icons/fi";
import { LuEye } from "react-icons/lu";
import { HiOutlineTrash } from "react-icons/hi2";
import TechnicianInsightsPanel from "./TechnicianInsightsPanel";
import TechnicianPresentationsTab from "./TechnicianPresentationsTab";
import { Link } from "react-router-dom";

/* =========================
   HELPERS
========================= */
function normalize(str) {
  return str ? str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase() : "";
}

const safeValue = (v, fallback = "—") => (v === null || v === undefined || v === "" ? fallback : v);

const bytesToSize = (bytes = 0) => {
  const n = Number(bytes || 0);
  if (!n) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(n) / Math.log(k));
  return `${(n / Math.pow(k, i)).toFixed(i === 0 ? 0 : 1)} ${sizes[i]}`;
};

const isImageMime = (mime = "") => /^image\//i.test(mime);
const isPdfMime  = (mime = "") => /pdf/i.test(mime);

const MAX_FILE_MB    = 5;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

const stringToColor = (string) => {
  if (!string) return "#9ca3af";
  let hash = 0;
  for (let i = 0; i < string.length; i++) hash = string.charCodeAt(i) + ((hash << 5) - hash);
  let color = "#";
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ("00" + value.toString(16)).slice(-2);
  }
  return color;
};

const getStatusClass = (status) => {
  const s = normalize(status);
  if (s === "ativo")              return "s-ativo";
  if (s === "inativo")            return "s-inativo";
  if (s === "ferias" || s === "férias") return "s-ferias";
  return "s-outro";
};

const statusOptions = [
  { label: "Ativo",   value: "ativo" },
  { label: "Inativo", value: "inativo" },
  { label: "Férias",  value: "ferias" },
];

const trainingStatusOptions = [
  { label: "Planeada",  value: "PLANEADA"  },
  { label: "Dada",      value: "DADA"      },
  { label: "Cancelada", value: "CANCELADA" },
];

const trainingStatusBadgeClass = (st = "") => {
  const s = String(st || "").toUpperCase();
  if (s === "DADA")      return "tr-dada";
  if (s === "CANCELADA") return "tr-cancelada";
  return "tr-planeada";
};

const mapTech = (t) => ({
  id:            t?.ed_tech_id      ?? t?.tech_id      ?? t?.id,
  name:          t?.ed_tech_name    ?? t?.tech_name    ?? t?.name,
  email:         t?.ed_tech_email   ?? t?.tech_email   ?? t?.email,
  phone:         t?.ed_tech_phone   ?? t?.tech_phone   ?? t?.phone,
  area:          t?.ed_tech_area    ?? t?.tech_area    ?? t?.area,
  notes:         t?.ed_tech_notes   ?? t?.tech_notes   ?? t?.notes,
  status:        t?.ed_tech_status  ?? t?.tech_status  ?? t?.status ?? "ativo",
  province:      t?.ed_tech_province ?? t?.tech_province ?? t?.province ?? "",
  schools_count: t?.assigned_schools_count ?? t?.schools_count ?? 0,
  picture:
    t?.ed_tech_picture_url ??
    t?.ed_tech_picture     ??
    t?.tech_picture        ??
    t?.picture             ??
    t?.ed_tech_photo       ?? "",
});

/* =========================
   SELECT SEARCH COMPONENT
   — dropdown com pesquisa inline, sem dependências externas —
========================= */
/**
 * Props:
 *   options      : [{ value, label, sublabel?, badge?, badgeColor? }]
 *   value        : string (valor seleccionado)
 *   onChange     : fn(value) => void
 *   placeholder  : string
 *   disabled     : bool
 *   emptyMsg     : string (quando não há resultados)
 */
const SelectSearch = ({
  options = [],
  value = "",
  onChange,
  placeholder = "Pesquisar…",
  disabled = false,
  emptyMsg = "Nenhum resultado",
}) => {
  const [open, setOpen]     = useState(false);
  const [query, setQuery]   = useState("");
  const containerRef        = useRef(null);
  const inputRef            = useRef(null);

  // fecha ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = normalize(query);
    return options.filter(
      (o) => normalize(o.label).includes(q) || normalize(o.sublabel || "").includes(q)
    );
  }, [options, query]);

  const selected = options.find((o) => o.value === value);

  const handleSelect = (opt) => {
    onChange(opt.value);
    setOpen(false);
    setQuery("");
  };

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const licenceColor = (days) => {
    const d = Number(days ?? -1);
    if (d <= 0)   return "#ef4444";
    if (d <= 30)  return "#f59e0b";
    return "#22c55e";
  };

  return (
    <div ref={containerRef} style={{ position: "relative", width: "100%" }}>
      {/* trigger */}
      <div
        onClick={handleOpen}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: "8px 13px",
          borderRadius: 12,
          border: `1.5px solid ${open ? "rgba(91,92,255,.45)" : "rgba(226,232,240,.95)"}`,
          background: disabled ? "#f8fafc" : "#fff",
          cursor: disabled ? "not-allowed" : "pointer",
          boxShadow: open ? "0 0 0 .2rem rgba(91,92,255,.15)" : "none",
          transition: "border-color .15s, box-shadow .15s",
          minHeight: 42,
          opacity: disabled ? 0.6 : 1,
        }}
      >
        {selected ? (
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: ".86rem", color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {selected.label}
            </div>
            {selected.sublabel && (
              <div style={{ fontSize: ".72rem", color: "#64748b", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {selected.sublabel}
              </div>
            )}
          </div>
        ) : (
          <span style={{ color: "#94a3b8", fontSize: ".86rem" }}>{placeholder}</span>
        )}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5"
          style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform .15s" }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>

      {/* dropdown */}
      {open && (
        <div style={{
          position: "absolute",
          top: "calc(100% + 4px)",
          left: 0,
          right: 0,
          zIndex: 9999,
          background: "#fff",
          border: "1px solid rgba(226,232,240,.95)",
          borderRadius: 14,
          boxShadow: "0 12px 40px rgba(15,23,42,.12)",
          overflow: "hidden",
        }}>
          {/* search input */}
          <div style={{ padding: "8px 10px", borderBottom: "1px solid rgba(226,232,240,.8)", background: "#fafbfe" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7,
              background: "#fff", borderRadius: 10, border: "1px solid rgba(226,232,240,.95)", padding: "6px 10px" }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Pesquisar…"
                style={{ border: "none", outline: "none", flex: 1, fontSize: ".83rem", color: "#0f172a", background: "transparent" }}
              />
              {query && (
                <button onClick={() => setQuery("")} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 0 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* list */}
          <div style={{ maxHeight: 260, overflowY: "auto" }}>
            {/* "limpar seleção" se já houver valor */}
            {value && (
              <div
                onClick={() => handleSelect({ value: "" })}
                style={{ padding: "8px 13px", fontSize: ".8rem", color: "#94a3b8", cursor: "pointer", borderBottom: "1px solid rgba(226,232,240,.5)" }}
                onMouseEnter={e => e.currentTarget.style.background = "#f8fafc"}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                — Limpar seleção —
              </div>
            )}

            {filtered.length === 0 ? (
              <div style={{ padding: "14px 13px", color: "#94a3b8", fontSize: ".83rem", textAlign: "center" }}>{emptyMsg}</div>
            ) : (
              filtered.map((opt) => (
                <div
                  key={opt.value}
                  onClick={() => handleSelect(opt)}
                  style={{
                    padding: "9px 13px",
                    cursor: "pointer",
                    background: opt.value === value ? "rgba(91,92,255,.06)" : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    borderBottom: "1px solid rgba(226,232,240,.4)",
                    transition: "background .1s",
                  }}
                  onMouseEnter={e => { if (opt.value !== value) e.currentTarget.style.background = "rgba(148,163,184,.08)"; }}
                  onMouseLeave={e => { if (opt.value !== value) e.currentTarget.style.background = "transparent"; }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: ".84rem", color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {opt.value === value && (
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5b5cff" strokeWidth="3" style={{ marginRight: 5 }}>
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                      {opt.label}
                    </div>
                    {opt.sublabel && (
                      <div style={{ fontSize: ".72rem", color: "#64748b", marginTop: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {opt.sublabel}
                      </div>
                    )}
                  </div>

                  {/* badge (ex.: dias restantes de licença) */}
                  {opt.badge !== undefined && opt.badge !== null && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 3,
                      padding: "2px 8px", borderRadius: 999,
                      fontSize: ".7rem", fontWeight: 700, flexShrink: 0,
                      background: `${licenceColor(opt.badgeDays ?? (opt.badge === "—" ? -1 : opt.badge))}20`,
                      color: licenceColor(opt.badgeDays ?? (opt.badge === "—" ? -1 : opt.badge)),
                      border: `1px solid ${licenceColor(opt.badgeDays ?? (opt.badge === "—" ? -1 : opt.badge))}40`,
                    }}>
                      {opt.badge}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* =========================
   STYLES
========================= */
const uiStyles = `
:root{
  --primary:#5b5cff;
  --primary2:#7c3aed;
  --bg:#f4f6fb;
  --card:#ffffff;
  --border:#e6eaf2;
  --text:#0f172a;
  --muted:#64748b;
  --shadow: 0 14px 40px rgba(15,23,42,.08);
}
.shell{ background:var(--bg); padding:10px; }
.cardx{
  background:var(--card);
  border-radius:18px;
  border:1px solid var(--border);
  padding:12px 14px 14px;
  box-shadow: var(--shadow);
}
.headerx{
  display:flex; flex-wrap:wrap;
  justify-content:space-between;
  align-items:flex-start;
  gap:10px; margin-bottom:10px;
}
.header-title{
  font-weight:700; font-size:1.02rem;
  display:flex; align-items:center; gap:.55rem;
  color:var(--text);
}
.chip{
  display:inline-flex; align-items:center; gap:.35rem;
  padding:.28rem .62rem; border-radius:999px;
  background:linear-gradient(135deg, rgba(91,92,255,.14), rgba(124,58,237,.12));
  color:#4338ca; font-size:.78rem; font-weight:600;
  border:1px solid rgba(91,92,255,.16);
}
.toolbar{
  margin-top:6px;
  padding:10px 10px;
  border-radius:14px;
  background:linear-gradient(180deg, rgba(148,163,184,0.12), rgba(148,163,184,0.06));
  border:1px solid rgba(226,232,240,.7);
  margin-bottom:10px;
}
.kpi-row{
  display:grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap:10px;
  margin:10px 0 12px;
}
@media (max-width: 1200px){
  .kpi-row{ grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@media (max-width: 520px){
  .kpi-row{ grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
.kpi{
  background:var(--card);
  border:1px solid var(--border);
  border-radius:16px;
  padding:10px 12px;
  box-shadow: 0 10px 28px rgba(2,6,23,.06);
  position:relative;
  overflow:hidden;
}
.kpi::before{
  content:"";
  position:absolute;
  inset:-40px -60px auto auto;
  width:120px; height:120px;
  border-radius:999px;
  background: radial-gradient(circle at 30% 30%, rgba(91,92,255,.18), transparent 60%);
}
.kpi-title{ display:flex; align-items:center; gap:8px; font-size:.8rem; color:var(--muted); font-weight:600; }
.kpi-value{ margin-top:2px; font-size:1.18rem; font-weight:650; color:var(--text); }
.kpi-sub{ margin-top:2px; font-size:.74rem; color:var(--muted); }

.table-wrapper{ width:100%; }
@media (max-width:768px){
  .table-desktop{display:none;}
  .mobile-list{display:block;}
}
@media (min-width:769px){
  .table-desktop{display:block;}
  .mobile-list{display:none;}
}
.tablex{
  width:100%;
  border-collapse:separate;
  border-spacing:0;
  font-size:.86rem;
  color:var(--text);
}
.tablex thead th{
  text-align:left;
  padding:.62rem .65rem;
  font-size:.78rem;
  font-weight:650;
  color:var(--muted);
  border-bottom:1px solid var(--border);
  background:var(--card);
  position:sticky; top:0; z-index:1;
}
.tablex tbody td{
  padding:.62rem .65rem;
  border-bottom:1px solid var(--border);
  vertical-align:middle;
}
.tablex tbody tr:hover{ background:rgba(148,163,184,0.10); }

.pill{
  display:inline-flex; align-items:center; gap:6px;
  padding:4px 10px; border-radius:999px;
  font-size:.74rem; font-weight:600;
  border:1px solid rgba(15,23,42,.08);
  background:rgba(226,232,240,.55);
  color:#0f172a;
}
.avatar{
  width:28px;height:28px;border-radius:10px;
  display:inline-grid;place-items:center;
  font-size:.78rem;font-weight:700;color:#f9fafb;
  box-shadow: 0 10px 22px rgba(2,6,23,.10);
}
.status-badge{
  display:inline-flex;align-items:center;gap:8px;
  padding:6px 10px;border-radius:999px;
  font-size:.74rem;font-weight:650;
  border:1px solid rgba(0,0,0,.06);
  letter-spacing:.2px;
}
.status-dot{ width:8px;height:8px;border-radius:999px; background:currentColor; box-shadow:0 0 0 4px rgba(0,0,0,.04); }
.s-ativo{ background: rgba(34,197,94,.14); color:#22c55e; }
.s-inativo{ background: rgba(239,68,68,.14); color:#ef4444; }
.s-ferias{ background: rgba(245,158,11,.18); color:#f59e0b; }
.s-outro{ background: rgba(99,102,241,.14); color:#6366f1; }

.btn-icon{
  width:34px;height:34px;border-radius:12px;
  border:1px solid rgba(226,232,240,.95);
  background:#fff; display:inline-grid;place-items:center;
  cursor:pointer; transition:transform .08s ease, background .12s ease;
}
.btn-icon:hover{ background: rgba(91,92,255,.06); }
.btn-icon:active{ transform: scale(.98); }

.mobile-list{ margin-top:4px; }
.mcard{
  background:var(--card); border-radius:16px;
  border:1px solid var(--border);
  padding:12px 12px; margin:8px 0;
  box-shadow: 0 10px 26px rgba(2,6,23,.06);
}
.mrow{ display:flex; align-items:center; justify-content:space-between; gap:12px; }
.mline{ height:1px; background:var(--border); margin:10px 0; }
.small-muted{ font-size:.78rem; color:var(--muted); }

.resumo-card{
  margin-top:12px; background:var(--card);
  border-radius:18px; border:1px solid var(--border);
  padding:12px 14px 14px; box-shadow: var(--shadow);
}
.resumo-title{ font-weight:650; font-size:.98rem; margin-bottom:8px; color:var(--text); }
.resumo-table{ width:100%; border-collapse:collapse; font-size:.82rem; }
.resumo-table th,.resumo-table td{ border:1px solid var(--border); padding:.42rem .45rem; }
.resumo-table thead{ background:#f8fafc; }

/* ===== MODAL DESIGN ===== */
.pretty-modal .modal-dialog{ max-width: 920px; }
@media (max-width: 768px){ .pretty-modal .modal-dialog{ max-width: 96vw; } }
.pretty-modal .modal-content{
  border-radius: 18px;
  border: 1px solid rgba(226,232,240,.95);
  box-shadow: 0 30px 90px rgba(2,6,23,.22);
  overflow: hidden;
}
.pretty-modal .modal-header{
  border-bottom: 1px solid rgba(226,232,240,.9);
  background:
    radial-gradient(1000px 260px at 20% 0%, rgba(91,92,255,.16), transparent 60%),
    radial-gradient(900px 240px at 95% 0%, rgba(124,58,237,.12), transparent 55%),
    #ffffff;
  padding: 14px 16px;
}
.pretty-modal .modal-title{ font-weight: 650; color: #0f172a; font-size: 1rem; }
.pretty-modal .modal-body{
  padding: 14px 16px 12px;
  background: linear-gradient(180deg, rgba(248,250,252,.75), #ffffff 40%);
}
.body-scroll{ max-height: 560px; overflow-y: auto; padding-right: 4px; }
.pretty-modal .modal-footer{
  border-top: 1px solid rgba(226,232,240,.9);
  padding: 12px 16px; background: #ffffff;
}
.header-row{ display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.header-left{ display:flex; align-items:center; gap:12px; }
.avatar-wrap{
  width: 44px; height: 44px; border-radius: 14px;
  background: rgba(91,92,255,.10); border: 1px solid rgba(91,92,255,.18);
  display:grid; place-items:center;
}
.title-sub{ margin-top:2px; font-size: .8rem; color: #64748b; font-weight: 500; }
.form-grid{
  display:grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px 12px;
}
@media (max-width: 720px){ .form-grid{ grid-template-columns: 1fr; } }
.cardlet{
  background: rgba(255,255,255,.9);
  border: 1px solid rgba(226,232,240,.95);
  border-radius: 16px; padding: 10px 12px;
}
.cardlet-title{
  font-size: .78rem; color:#64748b; font-weight: 600;
  display:flex; align-items:center; gap:8px; margin-bottom: 8px;
}
.req{ color:#ef4444; font-weight:700; }
.form-label{
  font-size: .78rem !important; color: #475569 !important;
  font-weight: 600 !important; margin-bottom: 6px !important;
}
.form-control, .form-select{
  border-radius: 12px !important;
  border-color: rgba(226,232,240,.95) !important;
  padding-top: .55rem !important;
  padding-bottom: .55rem !important;
}
.form-control:focus, .form-select:focus{
  box-shadow: 0 0 0 .2rem rgba(91,92,255,.15) !important;
  border-color: rgba(91,92,255,.45) !important;
}
.helper{ font-size: .74rem; color:#64748b; margin-top: 6px; }
.actions{ display:flex; justify-content:flex-end; gap:10px; flex-wrap:wrap; }
.btn-soft{
  border-radius: 999px !important;
  border: 1px solid rgba(226,232,240,.95) !important;
  background: #fff !important;
}
.btn-primary{ border-radius: 999px !important; }

/* Docs */
.doc-row{ display:flex; gap:12px; align-items:flex-start; flex-wrap:wrap; }
.doc-preview{
  width: 86px; height: 86px; border-radius: 16px;
  border: 1px dashed rgba(148,163,184,.55);
  background: rgba(248,250,252,.75);
  display:grid; place-items:center; overflow:hidden;
}
.doc-preview img{ width:100%; height:100%; object-fit:cover; }
.doc-meta{ flex:1; min-width: 220px; }
.doc-actions{ display:flex; gap:8px; flex-wrap:wrap; justify-content:flex-end; }

/* FORMAÇÕES */
.tr-badge{
  display:inline-flex; align-items:center; gap:8px;
  padding:6px 10px; border-radius:999px;
  font-size:.74rem; font-weight:650;
  border:1px solid rgba(0,0,0,.06);
}
.tr-dot{ width:8px;height:8px;border-radius:999px; background:currentColor; box-shadow:0 0 0 4px rgba(0,0,0,.04); }
.tr-planeada{ background: rgba(99,102,241,.14); color:#6366f1; }
.tr-dada{ background: rgba(34,197,94,.14); color:#22c55e; }
.tr-cancelada{ background: rgba(239,68,68,.14); color:#ef4444; }

.gallery{
  display:grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap:10px;
}
@media (max-width: 880px){ .gallery{ grid-template-columns: repeat(3, minmax(0, 1fr)); } }
@media (max-width: 560px){ .gallery{ grid-template-columns: repeat(2, minmax(0, 1fr)); } }
.gitem{
  border:1px solid rgba(226,232,240,.95);
  border-radius:16px; overflow:hidden;
  background:#fff; box-shadow: 0 10px 26px rgba(2,6,23,.06);
}
.gthumb{
  width:100%; aspect-ratio: 1 / 1; display:block;
  object-fit:cover; background:#f1f5f9;
}
.gmeta{
  padding:8px 10px; display:flex;
  justify-content:space-between; gap:10px; align-items:center;
}
.gname{
  font-size:.74rem; color:#334155; font-weight:600;
  overflow:hidden; text-overflow:ellipsis; white-space:nowrap; max-width: 190px;
}

/* Licence badge colours (inline) handled via JS */
`;

/* =========================
   COMPONENT
========================= */
export default function TechniciansListTable() {
  const API_TECH             = useMemo(() => Hoot() + "api/technicians", []);
  const API_DOCS_LIST        = (techId) => `${API_TECH}/${techId}/documents`;
  const API_DOCS_DOWNLOAD    = (docId)  => Hoot() + `api/technicians/documents/${docId}/download`;
  const API_DOCS_DELETE      = (docId)  => Hoot() + `api/technicians/documents/${docId}`;
  const API_TRAININGS        = useMemo(() => Hoot() + "api/tech-trainings", []);
  const API_TRAINING_ONE     = (id)     => `${API_TRAININGS}/${id}`;
  const API_TRAINING_IMAGES  = (id)     => `${API_TRAININGS}/${id}/images`;
  const API_TRAINING_IMAGE_DELETE = (imgId) => `${API_TRAININGS}/images/${imgId}`;
  const API_TECH_SCHOOLS_LIST   = (techId) => Hoot() + `api/technicians/${techId}/schools`;
  const API_TECH_SCHOOLS_ASSIGN = (techId) => Hoot() + `api/technicians/${techId}/schools`;
  const API_TECH_SCHOOLS_REMOVE = (techId, assignId) => Hoot() + `api/technicians/${techId}/schools/${assignId}`;
  const API_ALL_INSTITUTES   = useMemo(() => Hoot() + "institutes", []);
  const API_CONTACTS         = useMemo(() => Hoot() + "contacts/list", []);

  /* ---- MAIN LIST ---- */
  const [techsRaw,       setTechsRaw]       = useState([]);
  const [activeTab,      setActiveTab]       = useState("listagem");
  const [search,         setSearch]         = useState("");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedProvince, setSelectedProvince] = useState("all");
  const [currentPage,    setCurrentPage]    = useState(1);
  const itemsPerPage = 10;

  /* ---- ALL INSTITUTES (for SelectSearch) ---- */
  const [allInstitutes, setAllInstitutes] = useState([]);

  /* ---- ALL CONTACTS (for presentations SelectSearch) ---- */
  const [allContacts, setAllContacts] = useState([]);

  /* ---- CRUD MODALS ---- */
  const initialTechForm = { ed_tech_name:"", ed_tech_phone:"", ed_tech_email:"", ed_tech_area:"", ed_tech_notes:"" };
  const [showUpsertModal, setShowUpsertModal] = useState(false);
  const [upsertLoading,   setUpsertLoading]   = useState(false);
  const [upsertError,     setUpsertError]     = useState(null);
  const [isUpdate,        setIsUpdate]        = useState(false);
  const [editId,          setEditId]          = useState(null);
  const [techForm,        setTechForm]        = useState(initialTechForm);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading,   setDeleteLoading]   = useState(false);
  const [deleteError,     setDeleteError]     = useState(null);
  const [deleteTarget,    setDeleteTarget]    = useState(null);

  /* ---- PROFILE MODAL ---- */
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileTab,       setProfileTab]       = useState("perfil");
  const [profileTech,      setProfileTech]      = useState(null);

  // Foto
  const [photoFile,    setPhotoFile]    = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoSaving,  setPhotoSaving]  = useState(false);
  const [photoError,   setPhotoError]   = useState(null);

  // Docs
  const [docsLoading,    setDocsLoading]    = useState(false);
  const [docsError,      setDocsError]      = useState(null);
  const [docs,           setDocs]           = useState([]);
  const [docTitle,       setDocTitle]       = useState("");
  const [docFile,        setDocFile]        = useState(null);
  const [docPreviewUrl,  setDocPreviewUrl]  = useState(null);
  const [docUploading,   setDocUploading]   = useState(false);
  const [showDeleteDocModal,  setShowDeleteDocModal]  = useState(false);
  const [deleteDocLoading,    setDeleteDocLoading]    = useState(false);
  const [deleteDocError,      setDeleteDocError]      = useState(null);
  const [deleteDocTarget,     setDeleteDocTarget]     = useState(null);

  // Schools
  const [schoolsLoading,      setSchoolsLoading]      = useState(false);
  const [schoolsError,        setSchoolsError]        = useState(null);
  const [schools,             setSchools]             = useState([]);
  const [assignInstituteCode, setAssignInstituteCode] = useState(""); // value do SelectSearch
  const [assignLoading,       setAssignLoading]       = useState(false);

  // Stats
  const [statsLoading,  setStatsLoading]  = useState(false);
  const [profileStats,  setProfileStats]  = useState(null);

  /* ---- FORMAÇÕES ---- */
  const [trainingsLoading, setTrainingsLoading] = useState(false);
  const [trainingsError,   setTrainingsError]   = useState(null);
  const [trainings,        setTrainings]        = useState([]);

  const initialTrainingForm = {
    institute_code: "",
    date: "", status: "PLANEADA",
    description: "", logistics: "", cost_justification: "",
    cost_transport: "0", cost_food: "0", cost_other: "0",
  };
  const [trainingForm,    setTrainingForm]    = useState(initialTrainingForm);
  const [trainingCreating, setTrainingCreating] = useState(false);

  const [showTrainingModal,      setShowTrainingModal]      = useState(false);
  const [trainingModalLoading,   setTrainingModalLoading]   = useState(false);
  const [trainingModalError,     setTrainingModalError]     = useState(null);
  const [trainingModalData,      setTrainingModalData]      = useState(null);
  const [trainingSaving,         setTrainingSaving]         = useState(false);
  const [trainingImagesFiles,    setTrainingImagesFiles]    = useState([]);
  const [trainingImagesPreview,  setTrainingImagesPreview]  = useState([]);
  const [trainingImagesUploading, setTrainingImagesUploading] = useState(false);
  const [showDeleteTrainingModal, setShowDeleteTrainingModal] = useState(false);
  const [deleteTrainingTarget,    setDeleteTrainingTarget]    = useState(null);
  const [deleteTrainingLoading,   setDeleteTrainingLoading]   = useState(false);
  const [deleteTrainingError,     setDeleteTrainingError]     = useState(null);

  const revokeTrainingPreviews = () => {
    (trainingImagesPreview || []).forEach((u) => { try { URL.revokeObjectURL(u); } catch (_) { } });
  };

  /* =========================
     LOAD DATA
  ========================= */
  const loadData = async () => {
    try {
      const res = await axios.get(API_TECH, { withCredentials: true });
      const rows = res?.data?.data ?? res?.data ?? [];
      setTechsRaw(Array.isArray(rows) ? rows : []);
    } catch { toast.error("Erro ao carregar técnicos."); }
  };

  // carrega todas as instituições (inclui licença e dias restantes graças ao endpoint /institutes)
  const loadAllInstitutes = async () => {
    try {
      const res = await axios.get(API_ALL_INSTITUTES, { withCredentials: true });
      const rows = res?.data ?? [];
      setAllInstitutes(Array.isArray(rows) ? rows : []);
    } catch { /* silencioso — apenas afecta o SelectSearch */ }
  };

  // carrega todos os contactos (para o selector de apresentações)
  const loadAllContacts = async () => {
    try {
      const res = await axios.get(API_CONTACTS, { withCredentials: true });
      const rows = res?.data?.data ?? res?.data ?? [];
      setAllContacts(Array.isArray(rows) ? rows : []);
    } catch { /* silencioso */ }
  };

  useEffect(() => {
    loadData();
    loadAllInstitutes();
    loadAllContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =========================
     OPTIONS para SelectSearch
  ========================= */

  // Todas as escolas → opções para o selector de atribuição e formações
  const instituteOptions = useMemo(() => {
    return allInstitutes.map((inst) => {
      const days    = inst.licence?.remaining_days ?? null;
      const expDate = inst.licence?.end_date
        ? new Date(inst.licence.end_date).toLocaleDateString("pt-PT", { day:"2-digit", month:"2-digit", year:"numeric" })
        : "—";
      const licType = inst.licence?.type ?? "";
      return {
        value:     inst.code || String(inst.id),
        label:     inst.name || inst.code,
        sublabel:  `${inst.province || ""}${licType ? ` · ${licType}` : ""} · Expira: ${expDate}`,
        badge:     days !== null ? `${days}d` : "—",
        badgeDays: days,
      };
    });
  }, [allInstitutes]);

  // Escolas atribuídas ao técnico → opções para o selector de formações
  const assignedSchoolOptions = useMemo(() => {
    return schools.map((s) => ({
      value:    s.institute_code || s.code || "",
      label:    s.institute_name || s.name || s.institute_code || s.code || "—",
      sublabel: s.province || s.state || "",
    }));
  }, [schools]);

  // Contactos → opções para o selector de apresentações
  const contactOptions = useMemo(() => {
    return allContacts.map((c) => ({
      value:    String(c.ed_contact_id || c.id || ""),
      label:    c.ed_contact_name || c.name || "—",
      sublabel: [c.ed_contact_phone || c.phone, c.ed_contact_email || c.email, c.ed_contact_province || c.province].filter(Boolean).join(" · "),
    }));
  }, [allContacts]);

  /* =========================
     DERIVED STATE
  ========================= */
  const techs       = useMemo(() => techsRaw.map(mapTech), [techsRaw]);
  const provincesList = useMemo(() => {
    const s = new Set(techs.map((t) => t.province || "").filter(Boolean));
    return Array.from(s).sort((a, b) => a.localeCompare(b, "pt"));
  }, [techs]);

  const filtered = useMemo(() => {
    const searchNorm = normalize(search);
    return techs.filter((t) => {
      const matchesSearch =
        normalize(t.name).includes(searchNorm)     ||
        normalize(t.email).includes(searchNorm)    ||
        normalize(t.phone).includes(searchNorm)    ||
        normalize(t.province).includes(searchNorm) ||
        normalize(t.area).includes(searchNorm);
      const matchesStatus  = selectedStatus  === "all" || normalize(t.status)   === normalize(selectedStatus);
      const matchesProvince = selectedProvince === "all" || normalize(t.province) === normalize(selectedProvince);
      return matchesSearch && matchesStatus && matchesProvince;
    });
  }, [techs, search, selectedStatus, selectedProvince]);

  const kpis = useMemo(() => {
    let ativo = 0, inativo = 0, ferias = 0;
    filtered.forEach((t) => {
      const s = normalize(t.status);
      if (s === "ativo") ativo++;
      else if (s === "inativo") inativo++;
      else if (s === "ferias" || s === "férias") ferias++;
    });
    const totalSchools = filtered.reduce((acc, t) => acc + Number(t.schools_count || 0), 0);
    return { total: filtered.length, ativo, inativo, ferias, totalSchools,
      avgSchools: filtered.length ? (totalSchools / filtered.length).toFixed(1) : "0.0" };
  }, [filtered]);

  const pageCount  = Math.ceil(filtered.length / itemsPerPage) || 1;
  const paginated  = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const renderPageNumbers = () => {
    const pages = [];
    const maxButtons = 7;
    if (pageCount <= maxButtons) {
      for (let i = 1; i <= pageCount; i++) pages.push(i);
    } else {
      pages.push(1);
      let start = Math.max(2, currentPage - 1);
      let end   = Math.min(pageCount - 1, currentPage + 1);
      if (start > 2) pages.push("...");
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < pageCount - 1) pages.push("...");
      pages.push(pageCount);
    }
    return pages.map((p, idx) =>
      p === "..." ? (
        <li key={`e-${idx}`} className="page-item disabled"><span className="page-link">…</span></li>
      ) : (
        <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
          <button className="page-link" onClick={() => setCurrentPage(p)}>{p}</button>
        </li>
      )
    );
  };

  /* =========================
     ANALYTICS
  ========================= */
  const provinceMap = useMemo(() => {
    const map = {};
    filtered.forEach((t) => {
      const prov = t.province || "N/A";
      const st   = t.status   || "indefinido";
      if (!map[prov]) map[prov] = {};
      if (!map[prov][st]) map[prov][st] = 0;
      map[prov][st]++;
    });
    return map;
  }, [filtered]);

  const provinceNames = useMemo(() => Object.keys(provinceMap), [provinceMap]);
  const chartSeries = useMemo(() => statusOptions.map((s) => ({
    name: s.value,
    data: provinceNames.map((prov) => provinceMap[prov]?.[s.value] || 0),
  })), [provinceNames, provinceMap]);

  const chartOptions = useMemo(() => ({
    chart: { type: "bar", stacked: true, toolbar: { show: false }, height: 340 },
    plotOptions: { bar: { borderRadius: 8, columnWidth: "46%" } },
    dataLabels: { enabled: false },
    xaxis: { categories: provinceNames, labels: { rotate: -30 } },
    legend: { position: "bottom", labels: { colors: "#334155" } },
    grid: { borderColor: "#e2e8f0" },
    tooltip: { theme: "light", y: { formatter: (val) => `${val} técnicos` } },
  }), [provinceNames]);

  /* =========================
     BADGES / HELPERS
  ========================= */
  const StatusBadge = ({ status }) => (
    <span className={`status-badge ${getStatusClass(status)}`}>
      <span className="status-dot" />{safeValue(status, "Sem estado")}
    </span>
  );
  const TrainingStatusBadge = ({ status }) => (
    <span className={`tr-badge ${trainingStatusBadgeClass(status)}`}>
      <span className="tr-dot" />
      {trainingStatusOptions.find((o) => o.value === status)?.label || safeValue(status, "—")}
    </span>
  );

  /* =========================
     CRUD ACTIONS
  ========================= */
  const openCreateModal = () => {
    setIsUpdate(false); setEditId(null); setUpsertError(null);
    setTechForm(initialTechForm); setShowUpsertModal(true);
  };
  const closeUpsertModal = () => {
    if (upsertLoading) return;
    setShowUpsertModal(false); setUpsertLoading(false);
    setUpsertError(null); setTechForm(initialTechForm);
    setEditId(null); setIsUpdate(false);
  };
  const handleTechChange = (e) => {
    const { name, value } = e.target;
    setTechForm((p) => ({ ...p, [name]: value }));
  };
  const openEditModal = async (t) => {
    try {
      const id = t?.id;
      if (!id) return toast.error("ID do técnico inválido.");
      setIsUpdate(true); setEditId(id); setUpsertError(null);
      setUpsertLoading(true); setShowUpsertModal(true);
      const res = await axios.get(`${API_TECH}/${id}`, { withCredentials: true });
      const data = mapTech(res?.data?.data ?? res?.data ?? {});
      setTechForm({ ed_tech_name: data.name||"", ed_tech_phone: data.phone||"",
        ed_tech_email: data.email||"", ed_tech_area: data.area||"", ed_tech_notes: data.notes||"" });
    } catch { setUpsertError("Erro ao carregar dados do técnico."); }
    finally { setUpsertLoading(false); }
  };
  const submitUpsert = async (e) => {
    e?.preventDefault?.();
    if (upsertLoading) return;
    if (!techForm.ed_tech_name?.trim()) { setUpsertError("Nome do técnico é obrigatório."); return; }
    setUpsertLoading(true); setUpsertError(null);
    try {
      if (isUpdate && editId) {
        await axios.put(`${API_TECH}/${editId}`, techForm, { withCredentials: true });
        toast.success("Técnico atualizado!");
      } else {
        await axios.post(API_TECH, techForm, { withCredentials: true });
        toast.success("Técnico registado!");
      }
      closeUpsertModal(); await loadData();
    } catch (err) {
      setUpsertError(err?.response?.data?.msg || "Erro ao salvar técnico.");
      setUpsertLoading(false);
    }
  };
  const openDeleteConfirm = (t) => { setDeleteTarget(t); setDeleteError(null); setShowDeleteModal(true); };
  const closeDeleteModal  = () => {
    if (deleteLoading) return;
    setShowDeleteModal(false); setDeleteLoading(false); setDeleteError(null); setDeleteTarget(null);
  };
  const confirmDelete = async () => {
    try {
      if (!deleteTarget?.id) return setDeleteError("ID inválido.");
      setDeleteLoading(true); setDeleteError(null);
      await axios.delete(`${API_TECH}/${deleteTarget.id}`, { withCredentials: true });
      toast.success("Técnico removido!"); closeDeleteModal(); await loadData();
    } catch (err) { setDeleteError(err?.response?.data?.msg || "Erro."); setDeleteLoading(false); }
  };

  /* =========================
     PROFILE MODAL
  ========================= */
  const openProfile = async (t) => {
    setProfileTech(t); setShowProfileModal(true); setProfileTab("perfil");
    setPhotoFile(null); setPhotoError(null); setPhotoSaving(false);
    setPhotoPreview(t?.picture || null);
    loadDocs(t?.id); loadSchools(t?.id); loadStatsSilently(); loadTrainings(t?.id);
    setTrainingForm(initialTrainingForm);
  };
  const closeProfile = () => {
    const busy = photoSaving||docUploading||deleteDocLoading||assignLoading||trainingCreating||trainingImagesUploading||trainingSaving||deleteTrainingLoading;
    if (busy) return;
    setShowProfileModal(false); setProfileTech(null);
    setDocs([]); setDocsError(null); setDocTitle(""); setDocFile(null);
    if (docPreviewUrl) URL.revokeObjectURL(docPreviewUrl);
    setDocPreviewUrl(null);
    setSchools([]); setSchoolsError(null); setAssignInstituteCode(""); 
    setProfileStats(null);
    setTrainings([]); setTrainingsError(null); setTrainingForm(initialTrainingForm);
    setShowTrainingModal(false); setTrainingModalData(null); setTrainingModalError(null);
    revokeTrainingPreviews(); setTrainingImagesFiles([]); setTrainingImagesPreview([]);
  };

  const loadStatsSilently = async () => {
    try {
      setStatsLoading(true);
      const r = await axios.get(Hoot() + "profile/stats", { withCredentials: true });
      setProfileStats(r?.data || null);
    } catch { setProfileStats(null); }
    finally { setStatsLoading(false); }
  };

  /* ---- FOTO ---- */
  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    setPhotoError(null);
    if (!file) { setPhotoFile(null); setPhotoPreview(profileTech?.picture || null); return; }
    if (file.size > MAX_FILE_BYTES) {
      setPhotoFile(null); setPhotoPreview(profileTech?.picture || null);
      setPhotoError(`Fotografia excede ${MAX_FILE_MB}MB.`); return;
    }
    setPhotoFile(file); setPhotoPreview(URL.createObjectURL(file));
  };
  const savePhoto = async () => {
    if (!profileTech?.id || !photoFile) return toast.info("Seleciona uma fotografia.");
    setPhotoSaving(true); setPhotoError(null);
    try {
      const fd = new FormData();
      fd.append("ed_tech_picture", photoFile);
      fd.append("ed_tech_name",  profileTech.name  || "");
      fd.append("ed_tech_phone", profileTech.phone || "");
      fd.append("ed_tech_email", profileTech.email || "");
      fd.append("ed_tech_area",  profileTech.area  || "");
      fd.append("ed_tech_notes", profileTech.notes || "");
      await axios.put(`${API_TECH}/${profileTech.id}`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Fotografia atualizada!"); await loadData(); setPhotoFile(null);
    } catch (err) { setPhotoError(err?.response?.data?.msg || "Não foi possível guardar."); }
    finally { setPhotoSaving(false); }
  };

  /* ---- DOCS ---- */
  const loadDocs = async (techId) => {
    if (!techId) return;
    try {
      setDocsLoading(true); setDocsError(null);
      const res = await axios.get(API_DOCS_LIST(techId), { withCredentials: true });
      setDocs(Array.isArray(res?.data?.data) ? res.data.data : []);
    } catch (err) { setDocsError(err?.response?.data?.msg || "Erro ao carregar documentos."); setDocs([]); }
    finally { setDocsLoading(false); }
  };
  const onPickDocFile = (e) => {
    const file = e.target.files?.[0];
    setDocsError(null);
    if (docPreviewUrl) URL.revokeObjectURL(docPreviewUrl);
    setDocPreviewUrl(null);
    if (!file) { setDocFile(null); return; }
    if (file.size > MAX_FILE_BYTES) { setDocFile(null); setDocsError(`Ficheiro excede ${MAX_FILE_MB}MB.`); return; }
    setDocFile(file); setDocPreviewUrl(URL.createObjectURL(file));
    if (!docTitle) setDocTitle(file.name);
  };
  const uploadDoc = async () => {
    if (!profileTech?.id) return;
    if (!docFile) return setDocsError("Seleciona um ficheiro.");
    setDocUploading(true); setDocsError(null);
    try {
      const fd = new FormData();
      fd.append("file", docFile); fd.append("title", docTitle || docFile.name);
      await axios.post(API_DOCS_LIST(profileTech.id), fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento enviado!"); setDocTitle(""); setDocFile(null);
      if (docPreviewUrl) URL.revokeObjectURL(docPreviewUrl);
      setDocPreviewUrl(null); await loadDocs(profileTech.id);
    } catch (err) { setDocsError(err?.response?.data?.msg || "Erro ao enviar."); }
    finally { setDocUploading(false); }
  };
  const openDeleteDocConfirm = (doc) => { setDeleteDocTarget(doc); setDeleteDocError(null); setShowDeleteDocModal(true); };
  const closeDeleteDocModal  = () => {
    if (deleteDocLoading) return;
    setShowDeleteDocModal(false); setDeleteDocLoading(false); setDeleteDocError(null); setDeleteDocTarget(null);
  };
  const confirmDeleteDoc = async () => {
    try {
      if (!deleteDocTarget?.ed_tech_doc_id) return setDeleteDocError("ID inválido.");
      setDeleteDocLoading(true); setDeleteDocError(null);
      await axios.delete(API_DOCS_DELETE(deleteDocTarget.ed_tech_doc_id), { withCredentials: true });
      toast.success("Documento removido!"); closeDeleteDocModal(); await loadDocs(profileTech.id);
    } catch (err) { setDeleteDocError(err?.response?.data?.msg || "Erro."); setDeleteDocLoading(false); }
  };
  const downloadDoc = async (doc) => {
    try {
      const res = await axios.get(API_DOCS_DOWNLOAD(doc?.ed_tech_doc_id), { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: doc?.ed_tech_doc_mimetype || "application/octet-stream" }));
      const a   = document.createElement("a");
      a.href = url; a.download = (doc?.ed_tech_doc_title || "doc").replace(/[\\/:*?"<>|]+/g, "_");
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao fazer download."); }
  };
  const openDocPreview = (doc) => {
    const p = doc?.ed_tech_doc_path;
    if (!p) return toast.info("Sem caminho.");
    window.open(`${Hoot().slice(0, -1)}${p}`, "_blank");
  };

  /* ---- SCHOOLS ---- */
  const loadSchools = async (techId) => {
    if (!techId) return;
    setSchoolsLoading(true); setSchoolsError(null);
    try {
      const r   = await axios.get(API_TECH_SCHOOLS_LIST(techId), { withCredentials: true });
      const rows = r?.data?.data ?? r?.data ?? [];
      setSchools(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setSchools([]); setSchoolsError(err?.response?.data?.msg || "Erro ao carregar escolas.");
    } finally { setSchoolsLoading(false); }
  };

  const assignSchool = async () => {
    if (!profileTech?.id) return;
    const institute_code = (assignInstituteCode || "").trim();
    if (!institute_code) { setSchoolsError("Seleciona uma escola."); return; }
    setAssignLoading(true); setSchoolsError(null);
    try {
      await axios.post(API_TECH_SCHOOLS_ASSIGN(profileTech.id), { institute_code }, { withCredentials: true });
      toast.success("Escola atribuída!"); setAssignInstituteCode(""); await loadSchools(profileTech.id);
    } catch (err) { setSchoolsError(err?.response?.data?.msg || "Erro ao atribuir."); }
    finally { setAssignLoading(false); }
  };

  const removeSchool = async (assignId) => {
    if (!profileTech?.id || !assignId) return;
    setAssignLoading(true); setSchoolsError(null);
    try {
      await axios.delete(API_TECH_SCHOOLS_REMOVE(profileTech.id, assignId), { withCredentials: true });
      toast.success("Atribuição removida!"); await loadSchools(profileTech.id);
    } catch (err) { setSchoolsError(err?.response?.data?.msg || "Erro."); }
    finally { setAssignLoading(false); }
  };

  /* ---- FORMAÇÕES ---- */
  const loadTrainings = async (techId) => {
    if (!techId) return;
    try {
      setTrainingsLoading(true); setTrainingsError(null);
      const r    = await axios.get(`${API_TRAININGS}?tech_id=${techId}`, { withCredentials: true });
      const rows = r?.data ?? [];
      setTrainings(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setTrainings([]); setTrainingsError(err?.response?.data?.msg || "Erro ao carregar formações.");
    } finally { setTrainingsLoading(false); }
  };

  const onTrainingFormChange = (e) => {
    const { name, value } = e.target;
    setTrainingForm((p) => ({ ...p, [name]: value }));
  };

  const createTraining = async () => {
    if (!profileTech?.id) return;
    const payload = {
      tech_id:            profileTech.id,
      institute_code:     (trainingForm.institute_code || "").trim(),
      date:               trainingForm.date,
      status:             trainingForm.status || "PLANEADA",
      description:        trainingForm.description || "",
      logistics:          trainingForm.logistics || "",
      cost_justification: trainingForm.cost_justification || "",
      cost_transport:     Number(trainingForm.cost_transport || 0),
      cost_food:          Number(trainingForm.cost_food      || 0),
      cost_other:         Number(trainingForm.cost_other     || 0),
    };
    if (!payload.institute_code || !payload.date) { setTrainingsError("Escola e data são obrigatórios."); return; }
    setTrainingCreating(true); setTrainingsError(null);
    try {
      await axios.post(API_TRAININGS, payload, { withCredentials: true });
      toast.success("Formação criada!"); setTrainingForm(initialTrainingForm); await loadTrainings(profileTech.id);
    } catch (err) { setTrainingsError(err?.response?.data?.msg || "Erro ao criar."); }
    finally { setTrainingCreating(false); }
  };

  const openTrainingDetails = async (tr) => {
    const id = tr?.tr_id || tr?.id;
    if (!id) return;
    setShowTrainingModal(true); setTrainingModalLoading(true); setTrainingModalError(null); setTrainingModalData(null);
    revokeTrainingPreviews(); setTrainingImagesFiles([]); setTrainingImagesPreview([]);
    try {
      const r = await axios.get(API_TRAINING_ONE(id), { withCredentials: true });
      setTrainingModalData(r?.data || null);
    } catch (err) { setTrainingModalError(err?.response?.data?.msg || "Erro ao carregar."); }
    finally { setTrainingModalLoading(false); }
  };
  const closeTrainingModal = () => {
    if (trainingImagesUploading || trainingSaving) return;
    setShowTrainingModal(false); setTrainingModalData(null); setTrainingModalError(null);
    revokeTrainingPreviews(); setTrainingImagesFiles([]); setTrainingImagesPreview([]);
  };
  const updateTrainingStatus = async (trainingId, status) => {
    if (!trainingId) return;
    setTrainingSaving(true);
    try {
      const current = trainings.find((t) => t.tr_id === trainingId) || {};
      await axios.put(API_TRAINING_ONE(trainingId), {
        date: current.tr_date, status,
        description:        current.tr_description        || "",
        logistics:          current.tr_logistics          || "",
        cost_justification: current.tr_cost_justification || "",
        cost_transport:     Number(current.tr_cost_transport || 0),
        cost_food:          Number(current.tr_cost_food      || 0),
        cost_other:         Number(current.tr_cost_other     || 0),
      }, { withCredentials: true });
      toast.success("Estado atualizado!"); await loadTrainings(profileTech?.id);
      if (trainingModalData?.tr_id === trainingId) {
        const r = await axios.get(API_TRAINING_ONE(trainingId), { withCredentials: true });
        setTrainingModalData(r?.data || null);
      }
    } catch (err) { toast.error(err?.response?.data?.msg || "Erro."); }
    finally { setTrainingSaving(false); }
  };
  const openDeleteTrainingConfirm = (tr) => { setDeleteTrainingTarget(tr); setDeleteTrainingError(null); setShowDeleteTrainingModal(true); };
  const closeDeleteTrainingModal  = () => {
    if (deleteTrainingLoading) return;
    setShowDeleteTrainingModal(false); setDeleteTrainingTarget(null); setDeleteTrainingError(null);
  };
  const confirmDeleteTraining = async () => {
    const id = deleteTrainingTarget?.tr_id || deleteTrainingTarget?.id;
    if (!id) return setDeleteTrainingError("ID inválido.");
    setDeleteTrainingLoading(true); setDeleteTrainingError(null);
    try {
      await axios.delete(API_TRAINING_ONE(id), { withCredentials: true });
      toast.success("Formação removida!"); closeDeleteTrainingModal(); await loadTrainings(profileTech?.id);
      if (trainingModalData?.tr_id === id) closeTrainingModal();
    } catch (err) { setDeleteTrainingError(err?.response?.data?.msg || "Erro."); setDeleteTrainingLoading(false); }
  };

  const onPickTrainingImages = (e) => {
    const files = Array.from(e.target.files || []);
    setTrainingModalError(null); revokeTrainingPreviews(); setTrainingImagesFiles([]); setTrainingImagesPreview([]);
    if (!files.length) return;
    const tooBig = files.find((f) => f.size > MAX_FILE_BYTES);
    if (tooBig) { setTrainingModalError(`${tooBig.name} excede ${MAX_FILE_MB}MB.`); return; }
    setTrainingImagesFiles(files); setTrainingImagesPreview(files.map((f) => URL.createObjectURL(f)));
  };
  const uploadTrainingImages = async () => {
    const id = trainingModalData?.tr_id;
    if (!id || !trainingImagesFiles.length) { setTrainingModalError("Seleciona imagens."); return; }
    setTrainingImagesUploading(true); setTrainingModalError(null);
    try {
      const fd = new FormData();
      trainingImagesFiles.forEach((f) => fd.append("images", f));
      await axios.post(API_TRAINING_IMAGES(id), fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Imagens enviadas!"); revokeTrainingPreviews(); setTrainingImagesFiles([]); setTrainingImagesPreview([]);
      const r = await axios.get(API_TRAINING_ONE(id), { withCredentials: true });
      setTrainingModalData(r?.data || null); await loadTrainings(profileTech?.id);
    } catch (err) { setTrainingModalError(err?.response?.data?.msg || "Erro ao enviar."); }
    finally { setTrainingImagesUploading(false); }
  };
  const deleteTrainingImage = async (imageId) => {
    if (!imageId || !window.confirm("Remover esta imagem?")) return;
    try {
      await axios.delete(API_TRAINING_IMAGE_DELETE(imageId), { withCredentials: true });
      toast.success("Imagem removida!");
      const id = trainingModalData?.tr_id;
      if (id) { const r = await axios.get(API_TRAINING_ONE(id), { withCredentials: true }); setTrainingModalData(r?.data || null); }
      await loadTrainings(profileTech?.id);
    } catch (err) { toast.error(err?.response?.data?.msg || "Erro."); }
  };

  const isBusy = photoSaving||docUploading||deleteDocLoading||assignLoading||trainingCreating||trainingImagesUploading||trainingSaving||deleteTrainingLoading;

  /* =========================
     RENDER
  ========================= */
  return (
    <>
      <style>{uiStyles}</style>
      <div className="shell">
        <div className="cardx">

          {/* HEADER */}
          <div className="headerx">
            <div className="header-title">
              <i className="bi bi-person-workspace" style={{ color:"var(--primary)" }} />
              Técnicos
              <span className="chip"><i className="bi bi-people-fill" />{filtered.length} técnicos</span>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <button className="btn btn-primary btn-sm rounded-pill" onClick={openCreateModal}>
                <i className="bi bi-plus-lg me-1" />Adicionar técnico
              </button>
              <button className="btn btn-outline-dark btn-sm rounded-pill" onClick={loadData}>
                <i className="bi bi-arrow-clockwise me-1" />Recarregar
              </button>
          
               <Link to="/app" > 
               <button className="btn btn-outline-info text-info btn-sm rounded-pill" onClick={loadData}>
                <i className="bi bi-arrow-clockwise me-1" />Feramneta de emigração de dados
              </button>
               </Link>

            </div>
          </div>

          {/* TOOLBAR */}
          <div className="toolbar">
            <div className="row g-2">
              <div className="col-12 col-md-5">
                <input type="search" className="form-control form-control-sm rounded-pill"
                  placeholder="Pesquisar por nome, email, telefone, província, área…"
                  value={search} onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }} />
              </div>
              <div className="col-6 col-md-3">
                <select className="form-select form-select-sm rounded-pill" value={selectedStatus}
                  onChange={(e) => { setSelectedStatus(e.target.value); setCurrentPage(1); }}>
                  <option value="all">Todos os estados</option>
                  {statusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div className="col-6 col-md-3">
                <select className="form-select form-select-sm rounded-pill" value={selectedProvince}
                  onChange={(e) => { setSelectedProvince(e.target.value); setCurrentPage(1); }}>
                  <option value="all">Todas as províncias</option>
                  {provincesList.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="col-12 col-md-1 d-flex align-items-center justify-content-md-end">
                <small className="small-muted"><b>{currentPage}</b>/<b>{pageCount}</b></small>
              </div>
            </div>
          </div>

          {/* KPI */}
          <div className="kpi-row">
            {[
              { title:"Total (filtro)", icon:"bi-collection",    value: kpis.total,        sub:"Técnicos encontrados" },
              { title:"Ativos",         icon:"bi-check2-circle", value: kpis.ativo,        sub:"Disponíveis",         color:"#22c55e" },
              { title:"Inativos",       icon:"bi-x-circle",      value: kpis.inativo,      sub:"Sem atividade",       color:"#ef4444" },
              { title:"Férias",         icon:"bi-sun",           value: kpis.ferias,       sub:"Temporário",          color:"#f59e0b" },
              { title:"Escolas",        icon:"bi-buildings",     value: kpis.totalSchools,  sub:"Total no filtro" },
              { title:"Média",          icon:"bi-graph-up",      value: kpis.avgSchools,    sub:"Escolas/técnico" },
            ].map((k) => (
              <div key={k.title} className="kpi">
                <div className="kpi-title" style={k.color ? { color: k.color } : {}}>
                  <i className={`bi ${k.icon}`} />{k.title}
                </div>
                <div className="kpi-value">{k.value}</div>
                <div className="kpi-sub">{k.sub}</div>
              </div>
            ))}
          </div>

          <br />

          {/* TABS */}
          <Tabs activeKey={activeTab} onSelect={(k) => { setActiveTab(k || "listagem"); setCurrentPage(1); }} className="mb-2">

            {/* ── LISTAGEM ── */}
            <Tab eventKey="listagem" title="Listagem de técnicos">
              <div className="table-wrapper">
                <div className="table-desktop">
                  <table className="tablex">
                    <thead>
                      <tr>
                        <th>Técnico</th><th>Contacto</th><th>Área</th><th>Estado</th>
                        <th className="text-end">Ação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map((t) => (
                        <tr key={t.id}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {t.picture
                                ? <img src={t.picture} alt={t.name} className="avatar" />
                                : <div className="avatar" style={{ background: stringToColor(t.name||"?") }}>{(t.name||"?").charAt(0).toUpperCase()}</div>
                              }
                              <div>
                                <div style={{ fontWeight:600 }}>{safeValue(t.name,"Sem nome")}</div>
                                <div className="small-muted"><i className="bi bi-envelope me-1" />{safeValue(t.email,"Sem email")}</div>
                              </div>
                            </div>
                          </td>
                          <td><span className="pill"><i className="bi bi-telephone-fill" />{safeValue(t.phone,"Sem telefone")}</span></td>
                          <td><span className="pill"><i className="bi bi-tools" />{safeValue(t.area,"—")}</span></td>
                          <td><StatusBadge status={t.status} /></td>
                          <td className="text-end">
                            <div className="d-flex justify-content-end gap-2">
                              <button className="btn-icon" onClick={() => openProfile(t)} title="Perfil"><LuEye /></button>
                              <button className="btn-icon" onClick={() => openEditModal(t)} title="Editar"><FiEdit3 /></button>
                              <button className="btn-icon" onClick={() => openDeleteConfirm(t)} title="Remover"><HiOutlineTrash /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {paginated.length === 0 && (
                        <tr><td colSpan={5}><span className="small-muted">Nenhum técnico encontrado.</span></td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile */}
                <div className="mobile-list">
                  {paginated.map((t) => (
                    <div className="mcard" key={t.id}>
                      <div className="mrow">
                        <div className="d-flex align-items-center gap-2">
                          <div className="avatar" style={{ background: stringToColor(t.name||"T") }}>{(t.name||"T").charAt(0).toUpperCase()}</div>
                          <div>
                            <div style={{ fontSize:".82rem",fontWeight:600,color:"#0f172a" }}>{safeValue(t.name,"N/A")}</div>
                            <div className="small-muted">{safeValue(t.area,"—")}</div>
                          </div>
                        </div>
                        <StatusBadge status={t.status} />
                      </div>
                      <div className="mline" />
                      <div className="d-flex flex-wrap gap-2">
                        <span className="pill"><i className="bi bi-telephone-fill" />{safeValue(t.phone,"—")}</span>
                        <span className="pill"><i className="bi bi-envelope" />{safeValue(t.email,"—")}</span>
                      </div>
                      <div className="mline" />
                      <div className="d-flex justify-content-end gap-2">
                        <button className="btn-icon" onClick={() => openProfile(t)}><LuEye /></button>
                        <button className="btn-icon" onClick={() => openEditModal(t)}><FiEdit3 /></button>
                        <button className="btn-icon" onClick={() => openDeleteConfirm(t)}><HiOutlineTrash /></button>
                      </div>
                    </div>
                  ))}
                  {paginated.length === 0 && <span className="small-muted">Nenhum técnico.</span>}
                </div>
              </div>

              {/* PAGINAÇÃO */}
              <div className="contacts-footer">
                <nav>
                  <ul className="pagination pagination-sm mb-0">
                    <li className={`page-item ${currentPage===1?"disabled":""}`}>
                      <button className="page-link" onClick={() => setCurrentPage((p) => Math.max(1,p-1))}>«</button>
                    </li>
                    {renderPageNumbers()}
                    <li className={`page-item ${currentPage===pageCount?"disabled":""}`}>
                      <button className="page-link" onClick={() => setCurrentPage((p) => Math.min(pageCount,p+1))}>»</button>
                    </li>
                  </ul>
                </nav>
              </div>
            </Tab>

            <Tab eventKey="analises" title="Formações & Apresentações">
              <TechnicianInsightsPanel tech={profileTech} trainings={trainings} trainingsLoading={trainingsLoading} />
            </Tab>

            {/* ── ANÁLISE ── */}
            <Tab eventKey="analise" title="Análise (por província)">
              <div className="resumo-card">
                <div className="resumo-title d-flex justify-content-between align-items-center">
                  <span>Técnicos por província (stack por estado)</span>
                  <small className="small-muted">Cores = estados</small>
                </div>
                {provinceNames.length > 0
                  ? <ReactApexChart options={chartOptions} series={chartSeries} type="bar" height={340} width="100%" />
                  : <span className="small-muted">Sem dados suficientes.</span>
                }
                <div className="resumo-title mt-3">Resumo por província</div>
                <div className="table-responsive">
                  <table className="resumo-table">
                    <thead>
                      <tr>
                        <th>Província</th><th>Total</th>
                        {statusOptions.map((s) => <th key={s.value}>{s.label}</th>)}
                      </tr>
                    </thead>
                    <tbody>
                      {provinceNames.map((prov) => {
                        const total = Object.values(provinceMap[prov]||{}).reduce((a,b)=>a+b,0);
                        return (
                          <tr key={prov}>
                            <td style={{ fontWeight:600 }}>{prov}</td>
                            <td>{total}</td>
                            {statusOptions.map((s) => <td key={s.value}>{provinceMap[prov]?.[s.value]||0}</td>)}
                          </tr>
                        );
                      })}
                      {provinceNames.length===0 && <tr><td colSpan={2+statusOptions.length}><span className="small-muted">Sem dados.</span></td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </Tab>
          </Tabs>
        </div>

        {/* ═══════════════════════════════════════
            MODAL CRIAR / EDITAR
        ═══════════════════════════════════════ */}
        <Modal show={showUpsertModal} onHide={closeUpsertModal} centered backdrop="static" keyboard={!upsertLoading} className="pretty-modal">
          <Modal.Header closeButton={!upsertLoading}>
            <div className="header-row" style={{ width:"100%" }}>
              <div className="header-left">
                <div className="avatar-wrap">
                  <i className={`bi ${isUpdate?"bi-pencil-square":"bi-person-plus"}`} style={{ color:"#5b5cff",fontSize:18 }} />
                </div>
                <div>
                  <div style={{ fontWeight:650 }}>{isUpdate?"Atualizar técnico":"Registar técnico"}</div>
                  <div className="title-sub">Preenche os campos essenciais.</div>
                </div>
              </div>
              <div style={{ width:44,height:44,borderRadius:14,background:stringToColor(techForm.ed_tech_name||"T"),
                color:"#fff",display:"grid",placeItems:"center",fontWeight:800 }}>
                {(techForm.ed_tech_name||"T").charAt(0).toUpperCase()}
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="body-scroll">
              {upsertError && <Alert variant="danger" style={{ borderRadius:14 }}>{upsertError}</Alert>}
              <Form id="tech-form" onSubmit={submitUpsert}>
                <div className="form-grid">
                  <div className="cardlet" style={{ gridColumn:"1/-1" }}>
                    <div className="cardlet-title"><i className="bi bi-card-text" />Identificação</div>
                    <div className="form-grid">
                      <Form.Group>
                        <Form.Label>Nome <span className="req">*</span></Form.Label>
                        <Form.Control type="text" name="ed_tech_name" value={techForm.ed_tech_name} onChange={handleTechChange} required placeholder="Ex.: João Manuel" />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Área</Form.Label>
                        <Form.Control type="text" name="ed_tech_area" value={techForm.ed_tech_area} onChange={handleTechChange} placeholder="Ex.: Redes, Hardware..." />
                      </Form.Group>
                    </div>
                  </div>
                  <div className="cardlet">
                    <div className="cardlet-title"><i className="bi bi-telephone" />Contacto</div>
                    <Form.Group className="mb-2">
                      <Form.Label>Telefone</Form.Label>
                      <Form.Control type="text" name="ed_tech_phone" value={techForm.ed_tech_phone} onChange={handleTechChange} placeholder="+244 9xx xxx xxx" />
                    </Form.Group>
                    <Form.Group>
                      <Form.Label>Email</Form.Label>
                      <Form.Control type="email" name="ed_tech_email" value={techForm.ed_tech_email} onChange={handleTechChange} placeholder="tecnico@email.com" />
                    </Form.Group>
                  </div>
                  <div className="cardlet" style={{ gridColumn:"1/-1" }}>
                    <div className="cardlet-title"><i className="bi bi-journal-text" />Notas</div>
                    <Form.Group>
                      <Form.Label>Observações</Form.Label>
                      <Form.Control as="textarea" rows={4} name="ed_tech_notes" value={techForm.ed_tech_notes} onChange={handleTechChange} placeholder="Notas internas..." />
                    </Form.Group>
                  </div>
                </div>
              </Form>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="actions" style={{ width:"100%" }}>
              <Button variant="outline-secondary" className="btn-soft" onClick={closeUpsertModal} disabled={upsertLoading}>Cancelar</Button>
              <Button type="submit" variant="primary" form="tech-form" disabled={upsertLoading}>
                {upsertLoading ? <><Spinner animation="border" size="sm" className="me-2" />A guardar...</> : isUpdate ? "Atualizar" : "Registar"}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>

        {/* ═══════════════════════════════════════
            MODAL CONFIRM DELETE TÉCNICO
        ═══════════════════════════════════════ */}
        <Modal show={showDeleteModal} onHide={closeDeleteModal} centered backdrop="static" keyboard={!deleteLoading} className="pretty-modal">
          <Modal.Header closeButton={!deleteLoading}>
            <div className="header-row" style={{ width:"100%" }}>
              <div className="header-left">
                <div className="avatar-wrap" style={{ background:"rgba(239,68,68,.10)",borderColor:"rgba(239,68,68,.18)" }}>
                  <i className="bi bi-exclamation-triangle" style={{ color:"#ef4444",fontSize:18 }} />
                </div>
                <div>
                  <div style={{ fontWeight:650 }}>Confirmar remoção</div>
                  <div className="title-sub">Soft delete.</div>
                </div>
              </div>
              {deleteTarget && (
                <div style={{ width:44,height:44,borderRadius:14,background:stringToColor(deleteTarget.name||"T"),
                  color:"#fff",display:"grid",placeItems:"center",fontWeight:800 }}>
                  {(deleteTarget.name||"T").charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="body-scroll">
              {deleteError && <Alert variant="danger" style={{ borderRadius:14 }}>{deleteError}</Alert>}
              <div className="cardlet">
                <div className="cardlet-title"><i className="bi bi-trash3" />Técnico a remover</div>
                {deleteTarget
                  ? <><div style={{ fontWeight:650 }}>{safeValue(deleteTarget.name,"—")}</div><div className="helper">{safeValue(deleteTarget.email,"—")}</div></>
                  : <span className="small-muted">Sem técnico selecionado.</span>
                }
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="actions" style={{ width:"100%" }}>
              <Button variant="outline-secondary" className="btn-soft" onClick={closeDeleteModal} disabled={deleteLoading}>Cancelar</Button>
              <Button variant="danger" onClick={confirmDelete} disabled={deleteLoading}>
                {deleteLoading ? <><Spinner animation="border" size="sm" className="me-2" />A remover...</> : "Sim, remover"}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>

        {/* ═══════════════════════════════════════
            MODAL PERFIL (Tabs: Perfil | Escolas | Documentos | Formações | Apresentações)
        ═══════════════════════════════════════ */}
        <Modal show={showProfileModal} onHide={closeProfile} size="lg" centered backdrop="static"
          keyboard={!isBusy} className="pretty-modal">
          <Modal.Header closeButton={!isBusy}>
            <div className="header-row" style={{ width:"100%" }}>
              <div className="header-left">
                <div className="avatar-wrap">
                  <i className="bi bi-person-lines-fill" style={{ color:"#5b5cff",fontSize:18 }} />
                </div>
                <div>
                  <div style={{ fontWeight:650 }}>{profileTech ? safeValue(profileTech.name,"Perfil") : "Perfil do técnico"}</div>
                  <div className="title-sub">Fotografia, escolas, documentos e formações.</div>
                </div>
              </div>
              <div style={{ width:46,height:46,borderRadius:16,background:stringToColor(profileTech?.name||"T"),
                color:"#fff",display:"grid",placeItems:"center",fontWeight:800,overflow:"hidden" }}>
                {photoPreview
                  ? <img src={photoPreview} alt="Foto" style={{ width:"100%",height:"100%",objectFit:"cover" }} />
                  : (profileTech?.name||"T").charAt(0).toUpperCase()
                }
              </div>
            </div>
          </Modal.Header>

          <Modal.Body>
            <div className="body-scroll">
              <Tabs activeKey={profileTab} onSelect={(k) => setProfileTab(k||"perfil")} className="mb-2">

                {/* ── TAB PERFIL ── */}
                <Tab eventKey="perfil" title="Perfil">
                  <div className="form-grid">
                    <div className="cardlet" style={{ gridColumn:"1/-1" }}>
                      <div className="cardlet-title"><i className="bi bi-card-text" />Dados</div>
                      <div className="form-grid">
                        <div><div className="form-label">Nome</div><div>{safeValue(profileTech?.name)}</div></div>
                        <div><div className="form-label">Área</div><div>{safeValue(profileTech?.area)}</div></div>
                        <div><div className="form-label">Email</div><div>{safeValue(profileTech?.email,"Sem email")}</div></div>
                        <div><div className="form-label">Telefone</div><div>{safeValue(profileTech?.phone,"Sem telefone")}</div></div>
                        <div style={{ gridColumn:"1/-1" }}><div className="form-label">Notas</div><div>{safeValue(profileTech?.notes,"—")}</div></div>
                      </div>
                    </div>
                    <div className="cardlet">
                      <div className="cardlet-title"><i className="bi bi-badge-check" />Estado</div>
                      <StatusBadge status={profileTech?.status} />
                    </div>
                    <div className="cardlet">
                      <div className="cardlet-title"><i className="bi bi-graph-up" />Stats</div>
                      {statsLoading ? (
                        <div className="d-flex gap-2 small-muted"><Spinner animation="border" size="sm" />a carregar…</div>
                      ) : profileStats ? (
                        <div style={{ fontSize:".85rem" }}>
                          {[["Escolas",profileStats.schools_count],["Feedbacks",profileStats.feedbacks_count],
                            ["Docs",profileStats.docs_count],["Pagamentos",profileStats.payments_count],
                            ["Total líquido",Number(profileStats.payments_total_net||0).toFixed(0)]].map(([k,v]) => (
                            <div key={k} className="d-flex justify-content-between"><span>{k}</span><b>{v}</b></div>
                          ))}
                        </div>
                      ) : <div className="small-muted">Sem stats.</div>}
                    </div>
                    <div className="cardlet" style={{ gridColumn:"1/-1" }}>
                      <div className="cardlet-title"><i className="bi bi-image" />Fotografia (máx {MAX_FILE_MB}MB)</div>
                      {photoError && <Alert variant="danger" style={{ borderRadius:14 }}>{photoError}</Alert>}
                      <div className="doc-row">
                        <div className="doc-preview">
                          {photoPreview
                            ? <img src={photoPreview} alt="Preview" />
                            : <div className="small-muted" style={{ padding:10,textAlign:"center" }}>Sem foto</div>
                          }
                        </div>
                        <div className="doc-meta">
                          <Form.Label>Escolher fotografia</Form.Label>
                          <Form.Control type="file" accept="image/*" onChange={onPickPhoto} />
                          <div className="helper">Seleciona e clica em "Guardar foto".</div>
                        </div>
                        <div className="doc-actions">
                          <Button variant="outline-secondary" className="btn-soft" disabled={photoSaving}
                            onClick={() => { setPhotoFile(null); setPhotoError(null); setPhotoPreview(profileTech?.picture||null); }}>
                            Limpar
                          </Button>
                          <Button variant="primary" disabled={photoSaving||!photoFile} onClick={savePhoto}>
                            {photoSaving ? <><Spinner animation="border" size="sm" className="me-2" />A guardar...</> : "Guardar foto"}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Tab>

                {/* ── TAB ESCOLAS ── */}
                <Tab eventKey="escolas" title="Escolas">
                  {schoolsError && <Alert variant="danger" style={{ borderRadius:14,marginBottom:8 }}>{schoolsError}</Alert>}

                  <div className="cardlet">
                    <div className="cardlet-title"><i className="bi bi-buildings" />Atribuir escola</div>
                    <div className="helper" style={{ marginBottom:8 }}>
                      Pesquisa pelo nome da escola. Mostra o tipo de licença e os dias restantes.
                    </div>
                    <div className="form-grid">
                      <div style={{ gridColumn:"1/-1" }}>
                        <Form.Label>Escola <span className="req">*</span></Form.Label>
                        <SelectSearch
                          options={instituteOptions}
                          value={assignInstituteCode}
                          onChange={setAssignInstituteCode}
                          placeholder="Pesquisar escola…"
                          disabled={assignLoading}
                          emptyMsg="Nenhuma escola encontrada"
                        />
                        {/* legenda de cores */}
                        <div className="d-flex gap-3 mt-2" style={{ fontSize:".72rem",color:"#64748b" }}>
                          <span style={{ color:"#22c55e",fontWeight:600 }}>● Activa</span>
                          <span style={{ color:"#f59e0b",fontWeight:600 }}>● &lt; 30 dias</span>
                          <span style={{ color:"#ef4444",fontWeight:600 }}>● Expirada</span>
                        </div>
                      </div>
                      <div className="d-flex align-items-end justify-content-end">
                        <Button variant="primary" onClick={assignSchool} disabled={assignLoading||!assignInstituteCode}>
                          {assignLoading ? <><Spinner animation="border" size="sm" className="me-2" />A atribuir...</> : "Atribuir"}
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="cardlet mt-2">
                    <div className="cardlet-title"><i className="bi bi-list-check" />Escolas atribuídas</div>
                    {schoolsLoading ? (
                      <div className="d-flex gap-2 small-muted"><Spinner animation="border" size="sm" />a carregar…</div>
                    ) : schools.length === 0 ? (
                      <div className="small-muted">Sem escolas atribuídas.</div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm mb-0">
                          <thead><tr><th>Nome</th><th>Código</th><th>Estado</th><th className="text-end">Ação</th></tr></thead>
                          <tbody>
                            {schools.map((s) => (
                              <tr key={s.id||s.assign_id||s.institute_code||Math.random()}>
                                <td style={{ fontWeight:600 }}>{safeValue(s.institute_name||s.name,"—")}</td>
                                <td>{safeValue(s.institute_code||s.code,"—")}</td>
                                <td>{safeValue(s.state,"—")}</td>
                                <td className="text-end">
                                  <Button variant="outline-danger" size="sm" className="btn-soft"
                                    disabled={assignLoading||!(s.id||s.assign_id)}
                                    onClick={() => removeSchool(s.id||s.assign_id)}>
                                    Remover
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className="mt-2 d-flex justify-content-end">
                      <Button variant="outline-dark" size="sm" className="btn-soft"
                        onClick={() => loadSchools(profileTech?.id)} disabled={schoolsLoading}>
                        <i className="bi bi-arrow-clockwise me-1" />Recarregar
                      </Button>
                    </div>
                  </div>
                </Tab>

                {/* ── TAB DOCUMENTOS ── */}
                <Tab eventKey="documentos" title="Documentos">
                  {docsError && <Alert variant="danger" style={{ borderRadius:14,marginBottom:8 }}>{docsError}</Alert>}
                  <div className="cardlet">
                    <div className="cardlet-title"><i className="bi bi-cloud-upload" />Enviar documento (máx {MAX_FILE_MB}MB)</div>
                    <div className="doc-row">
                      <div className="doc-preview">
                        {docPreviewUrl ? (
                          isImageMime(docFile?.type) ? <img src={docPreviewUrl} alt="Preview" /> :
                          isPdfMime(docFile?.type)   ? <div style={{ textAlign:"center" }}><i className="bi bi-filetype-pdf" style={{ fontSize:34,color:"#ef4444" }} /><div className="small-muted">PDF</div></div> :
                          <div style={{ textAlign:"center" }}><i className="bi bi-file-earmark" style={{ fontSize:34,color:"#64748b" }} /><div className="small-muted">Ficheiro</div></div>
                        ) : <div className="small-muted" style={{ padding:10,textAlign:"center" }}>Sem ficheiro</div>}
                      </div>
                      <div className="doc-meta">
                        <Form.Group className="mb-2">
                          <Form.Label>Título</Form.Label>
                          <Form.Control value={docTitle} onChange={(e) => setDocTitle(e.target.value)} placeholder="Ex.: BI, Certificado..." disabled={docUploading} />
                        </Form.Group>
                        <Form.Group>
                          <Form.Label>Ficheiro</Form.Label>
                          <Form.Control type="file" onChange={onPickDocFile} disabled={docUploading} />
                          <div className="helper">Até {MAX_FILE_MB}MB.</div>
                        </Form.Group>
                        {docFile && <div className="helper"><b>{docFile.name}</b> · {bytesToSize(docFile.size)}</div>}
                      </div>
                      <div className="doc-actions">
                        <Button variant="outline-secondary" className="btn-soft" disabled={docUploading}
                          onClick={() => { setDocTitle(""); setDocFile(null); if(docPreviewUrl) URL.revokeObjectURL(docPreviewUrl); setDocPreviewUrl(null); }}>
                          Limpar
                        </Button>
                        <Button variant="primary" disabled={docUploading||!docFile} onClick={uploadDoc}>
                          {docUploading ? <><Spinner animation="border" size="sm" className="me-2" />A enviar...</> : "Enviar"}
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="cardlet mt-2">
                    <div className="cardlet-title"><i className="bi bi-folder2-open" />Lista de documentos</div>
                    {docsLoading ? (
                      <div className="d-flex gap-2 small-muted"><Spinner animation="border" size="sm" />a carregar…</div>
                    ) : docs.length === 0 ? (
                      <div className="small-muted">Sem documentos.</div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm mb-0">
                          <thead><tr><th>Título</th><th>Tipo</th><th>Tamanho</th><th className="text-end">Ações</th></tr></thead>
                          <tbody>
                            {docs.map((d) => (
                              <tr key={d.ed_tech_doc_id}>
                                <td style={{ fontWeight:600 }}>{safeValue(d.ed_tech_doc_title,"Documento")}</td>
                                <td>{safeValue(d.ed_tech_doc_mimetype,"—")}</td>
                                <td>{bytesToSize(d.ed_tech_doc_size)}</td>
                                <td className="text-end">
                                  <div className="d-flex gap-2 justify-content-end flex-wrap">
                                    <Button variant="outline-primary" size="sm" className="btn-soft" onClick={() => openDocPreview(d)}>Ver</Button>
                                    <Button variant="outline-dark" size="sm" className="btn-soft" onClick={() => downloadDoc(d)}>Download</Button>
                                    <Button variant="outline-danger" size="sm" className="btn-soft" onClick={() => openDeleteDocConfirm(d)}>Apagar</Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className="mt-2 d-flex justify-content-end">
                      <Button variant="outline-dark" size="sm" className="btn-soft" onClick={() => loadDocs(profileTech?.id)} disabled={docsLoading}>
                        <i className="bi bi-arrow-clockwise me-1" />Recarregar
                      </Button>
                    </div>
                  </div>
                </Tab>

                {/* ── TAB FORMAÇÕES ── */}
                <Tab eventKey="formacoes" title="Formações">
                  {trainingsError && <Alert variant="danger" style={{ borderRadius:14,marginBottom:8 }}>{trainingsError}</Alert>}
                  <div className="cardlet">
                    <div className="cardlet-title"><i className="bi bi-mortarboard" />Registar formação</div>
                    <div className="helper" style={{ marginBottom:8 }}>
                      Seleciona uma das escolas atribuídas a este técnico.
                    </div>
                    <div className="form-grid">
                      <Form.Group style={{ gridColumn:"1/-1" }}>
                        <Form.Label>Escola <span className="req">*</span></Form.Label>
                        {/* SelectSearch com escolas já atribuídas ao técnico */}
                        <SelectSearch
                          options={assignedSchoolOptions.length > 0 ? assignedSchoolOptions : instituteOptions}
                          value={trainingForm.institute_code}
                          onChange={(v) => setTrainingForm((p) => ({ ...p, institute_code: v }))}
                          placeholder={assignedSchoolOptions.length > 0 ? "Escolher escola atribuída…" : "Pesquisar escola…"}
                          disabled={trainingCreating}
                          emptyMsg="Nenhuma escola encontrada"
                        />
                        {assignedSchoolOptions.length === 0 && (
                          <div className="helper" style={{ color:"#f59e0b" }}>
                            ⚠️ Não há escolas atribuídas — a mostrar todas as escolas.
                          </div>
                        )}
                      </Form.Group>

                      <Form.Group>
                        <Form.Label>Data <span className="req">*</span></Form.Label>
                        <Form.Control name="date" type="date" value={trainingForm.date} onChange={onTrainingFormChange} disabled={trainingCreating} />
                      </Form.Group>
                      <Form.Group>
                        <Form.Label>Estado</Form.Label>
                        <Form.Select name="status" value={trainingForm.status} onChange={onTrainingFormChange} disabled={trainingCreating}>
                          {trainingStatusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </Form.Select>
                      </Form.Group>

                      <div className="d-flex align-items-end justify-content-end">
                        <Button variant="primary" onClick={createTraining} disabled={trainingCreating}>
                          {trainingCreating ? <><Spinner animation="border" size="sm" className="me-2" />A guardar...</> : "Registar"}
                        </Button>
                      </div>

                      <Form.Group style={{ gridColumn:"1/-1" }}>
                        <Form.Label>Descrição</Form.Label>
                        <Form.Control as="textarea" rows={3} name="description" value={trainingForm.description} onChange={onTrainingFormChange} disabled={trainingCreating} placeholder="Resumo da formação..." />
                      </Form.Group>
                      <Form.Group style={{ gridColumn:"1/-1" }}>
                        <Form.Label>Logística</Form.Label>
                        <Form.Control as="textarea" rows={2} name="logistics" value={trainingForm.logistics} onChange={onTrainingFormChange} disabled={trainingCreating} placeholder="Transporte, materiais..." />
                      </Form.Group>

                      <div className="cardlet" style={{ gridColumn:"1/-1",padding:10 }}>
                        <div className="cardlet-title"><i className="bi bi-cash-coin" />Custos (opcional)</div>
                        <div className="form-grid">
                          <Form.Group><Form.Label>Transporte</Form.Label><Form.Control name="cost_transport" value={trainingForm.cost_transport} onChange={onTrainingFormChange} disabled={trainingCreating} /></Form.Group>
                          <Form.Group><Form.Label>Alimentação</Form.Label><Form.Control name="cost_food" value={trainingForm.cost_food} onChange={onTrainingFormChange} disabled={trainingCreating} /></Form.Group>
                          <Form.Group><Form.Label>Outros</Form.Label><Form.Control name="cost_other" value={trainingForm.cost_other} onChange={onTrainingFormChange} disabled={trainingCreating} /></Form.Group>
                          <Form.Group style={{ gridColumn:"1/-1" }}>
                            <Form.Label>Justificação</Form.Label>
                            <Form.Control as="textarea" rows={2} name="cost_justification" value={trainingForm.cost_justification} onChange={onTrainingFormChange} disabled={trainingCreating} placeholder="Justificação de custos..." />
                          </Form.Group>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="cardlet mt-2">
                    <div className="cardlet-title"><i className="bi bi-list-task" />Lista de formações</div>
                    {trainingsLoading ? (
                      <div className="d-flex gap-2 small-muted"><Spinner animation="border" size="sm" />a carregar…</div>
                    ) : trainings.length === 0 ? (
                      <div className="small-muted">Sem formações registadas.</div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-sm mb-0">
                          <thead><tr><th>Data</th><th>Escola</th><th>Estado</th><th>Imagens</th><th className="text-end">Ações</th></tr></thead>
                          <tbody>
                            {trainings.map((tr) => (
                              <tr key={tr.tr_id}>
                                <td style={{ fontWeight:650 }}>
                                  {tr.tr_date ? new Date(tr.tr_date).toLocaleDateString("pt-PT",{day:"2-digit",month:"2-digit",year:"numeric"}) : "—"}
                                </td>
                                <td>
                                  <div style={{ fontWeight:650 }}>{safeValue(tr.institute_name,"—")}</div>
                                  <div className="small-muted">{safeValue(tr.tr_institute_code,"—")}</div>
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <TrainingStatusBadge status={tr.tr_status} />
                                    <Form.Select size="sm" style={{ width:160 }} value={tr.tr_status} disabled={trainingSaving}
                                      onChange={(e) => updateTrainingStatus(tr.tr_id, e.target.value)}>
                                      {trainingStatusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                    </Form.Select>
                                  </div>
                                </td>
                                <td>{Number(tr.images_count||0)}</td>
                                <td className="text-end">
                                  <div className="d-flex gap-2 justify-content-end flex-wrap">
                                    <Button variant="outline-primary" size="sm" className="btn-soft" onClick={() => openTrainingDetails(tr)}>Ver imagens</Button>
                                    <Button variant="outline-danger" size="sm" className="btn-soft" onClick={() => openDeleteTrainingConfirm(tr)}>Apagar</Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    <div className="mt-2 d-flex justify-content-end">
                      <Button variant="outline-dark" size="sm" className="btn-soft" onClick={() => loadTrainings(profileTech?.id)} disabled={trainingsLoading}>
                        <i className="bi bi-arrow-clockwise me-1" />Recarregar
                      </Button>
                    </div>
                  </div>
                </Tab>

                {/* ── TAB APRESENTAÇÕES ── */}
                <Tab eventKey="apresentacoes" title="Apresentações">
                  {/* Passa contactOptions para o sub-componente usar no SelectSearch */}
                  <TechnicianPresentationsTab
                    tech={profileTech}
                    statusOptions={statusOptions}
                    contactOptions={contactOptions}
                    SelectSearch={SelectSearch}
                  />
                </Tab>

              </Tabs>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="actions" style={{ width:"100%" }}>
              <Button variant="outline-secondary" className="btn-soft" onClick={closeProfile} disabled={isBusy}>Fechar</Button>
            </div>
          </Modal.Footer>
        </Modal>

        {/* ═══════════════════════════════════════
            MODAL CONFIRM DELETE DOCUMENTO
        ═══════════════════════════════════════ */}
        <Modal show={showDeleteDocModal} onHide={closeDeleteDocModal} centered backdrop="static" keyboard={!deleteDocLoading} className="pretty-modal">
          <Modal.Header closeButton={!deleteDocLoading}>
            <div className="header-row" style={{ width:"100%" }}>
              <div className="header-left">
                <div className="avatar-wrap" style={{ background:"rgba(239,68,68,.10)",borderColor:"rgba(239,68,68,.18)" }}>
                  <i className="bi bi-trash3" style={{ color:"#ef4444",fontSize:18 }} />
                </div>
                <div><div style={{ fontWeight:650 }}>Remover documento</div><div className="title-sub">Apaga no disco.</div></div>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="body-scroll">
              {deleteDocError && <Alert variant="danger" style={{ borderRadius:14 }}>{deleteDocError}</Alert>}
              <div className="cardlet">
                <div className="cardlet-title"><i className="bi bi-file-earmark" />Documento</div>
                <div style={{ fontWeight:650 }}>{safeValue(deleteDocTarget?.ed_tech_doc_title,"Documento")}</div>
                <div className="helper">{safeValue(deleteDocTarget?.ed_tech_doc_mimetype,"—")} · {bytesToSize(deleteDocTarget?.ed_tech_doc_size)}</div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="actions" style={{ width:"100%" }}>
              <Button variant="outline-secondary" className="btn-soft" onClick={closeDeleteDocModal} disabled={deleteDocLoading}>Cancelar</Button>
              <Button variant="danger" onClick={confirmDeleteDoc} disabled={deleteDocLoading}>
                {deleteDocLoading ? <><Spinner animation="border" size="sm" className="me-2" />A remover...</> : "Sim, remover"}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>

        {/* ═══════════════════════════════════════
            MODAL DETALHES + IMAGENS FORMAÇÃO
        ═══════════════════════════════════════ */}
        <Modal show={showTrainingModal} onHide={closeTrainingModal} centered backdrop="static"
          keyboard={!(trainingImagesUploading||trainingSaving)} className="pretty-modal">
          <Modal.Header closeButton={!(trainingImagesUploading||trainingSaving)}>
            <div className="header-row" style={{ width:"100%" }}>
              <div className="header-left">
                <div className="avatar-wrap"><i className="bi bi-images" style={{ color:"#5b5cff",fontSize:18 }} /></div>
                <div><div style={{ fontWeight:650 }}>Imagens da formação</div><div className="title-sub">Ver, adicionar e apagar imagens.</div></div>
              </div>
              {trainingModalData && <TrainingStatusBadge status={trainingModalData.tr_status} />}
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="body-scroll">
              {trainingModalError && <Alert variant="danger" style={{ borderRadius:14 }}>{trainingModalError}</Alert>}
              {trainingModalLoading ? (
                <div className="d-flex gap-2 small-muted"><Spinner animation="border" size="sm" />a carregar…</div>
              ) : trainingModalData ? (
                <>
                  <div className="cardlet">
                    <div className="cardlet-title"><i className="bi bi-info-circle" />Dados</div>
                    <div className="form-grid">
                      <div>
                        <div className="form-label">Escola</div>
                        <div style={{ fontWeight:650 }}>{safeValue(trainingModalData.institute_name,"—")}</div>
                        <div className="small-muted">{safeValue(trainingModalData.tr_institute_code,"—")}</div>
                      </div>
                      <div><div className="form-label">Data</div><div style={{ fontWeight:650 }}>{safeValue(trainingModalData.tr_date,"—")}</div></div>
                      <div style={{ gridColumn:"1/-1" }}>
                        <div className="form-label">Estado (atualizar)</div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <Form.Select style={{ maxWidth:240 }} value={trainingModalData.tr_status} disabled={trainingSaving}
                            onChange={async (e) => await updateTrainingStatus(trainingModalData.tr_id, e.target.value)}>
                            {trainingStatusOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </Form.Select>
                          <small className="small-muted">Ao mudar, guarda automaticamente.</small>
                        </div>
                      </div>
                      <div style={{ gridColumn:"1/-1" }}><div className="form-label">Descrição</div><div>{safeValue(trainingModalData.tr_description,"—")}</div></div>
                      <div style={{ gridColumn:"1/-1" }}><div className="form-label">Logística</div><div>{safeValue(trainingModalData.tr_logistics,"—")}</div></div>
                    </div>
                  </div>
                  <div className="cardlet mt-2">
                    <div className="cardlet-title"><i className="bi bi-cloud-upload" />Adicionar imagens (máx {MAX_FILE_MB}MB)</div>
                    <div className="form-grid">
                      <Form.Group style={{ gridColumn:"1/-1" }}>
                        <Form.Control type="file" accept="image/*" multiple onChange={onPickTrainingImages} disabled={trainingImagesUploading} />
                        <div className="helper">Seleciona várias imagens e clica em "Enviar imagens".</div>
                      </Form.Group>
                      {trainingImagesPreview.length > 0 && (
                        <div style={{ gridColumn:"1/-1" }}>
                          <Carousel interval={null} indicators={trainingImagesPreview.length>1}>
                            {trainingImagesPreview.map((src,idx) => (
                              <Carousel.Item key={idx}>
                                <div style={{ width:"100%",borderRadius:16,overflow:"hidden",border:"1px solid rgba(226,232,240,.95)" }}>
                                  <img src={src} alt={`preview-${idx}`} style={{ width:"100%",maxHeight:320,objectFit:"contain",background:"#f8fafc" }} />
                                </div>
                              </Carousel.Item>
                            ))}
                          </Carousel>
                          <div className="helper">{trainingImagesFiles.length} ficheiro(s).</div>
                        </div>
                      )}
                      <div className="d-flex justify-content-end gap-2" style={{ gridColumn:"1/-1" }}>
                        <Button variant="outline-secondary" className="btn-soft" disabled={trainingImagesUploading}
                          onClick={() => { revokeTrainingPreviews(); setTrainingImagesFiles([]); setTrainingImagesPreview([]); }}>
                          Limpar
                        </Button>
                        <Button variant="primary" disabled={trainingImagesUploading||!trainingImagesFiles.length} onClick={uploadTrainingImages}>
                          {trainingImagesUploading ? <><Spinner animation="border" size="sm" className="me-2" />A enviar...</> : "Enviar imagens"}
                        </Button>
                      </div>
                    </div>
                  </div>
                  <div className="cardlet mt-2">
                    <div className="cardlet-title"><i className="bi bi-grid-3x3-gap" />Galeria</div>
                    {Array.isArray(trainingModalData.images) && trainingModalData.images.length ? (
                      <div className="gallery">
                        {trainingModalData.images.map((img) => (
                          <div className="gitem" key={img.tri_id}>
                            <img className="gthumb" src={img.tri_url} alt={img.tri_original_name||"imagem"}
                              onClick={() => window.open(img.tri_url,"_blank")} style={{ cursor:"pointer" }} />
                            <div className="gmeta">
                              <div className="gname">{safeValue(img.tri_original_name,"imagem")}</div>
                              <Button variant="outline-danger" size="sm" className="btn-soft" onClick={() => deleteTrainingImage(img.tri_id)}>Apagar</Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : <div className="small-muted">Sem imagens.</div>}
                  </div>
                </>
              ) : <div className="small-muted">Sem dados.</div>}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="actions" style={{ width:"100%" }}>
              <Button variant="outline-secondary" className="btn-soft" onClick={closeTrainingModal} disabled={trainingImagesUploading||trainingSaving}>Fechar</Button>
            </div>
          </Modal.Footer>
        </Modal>

        {/* ═══════════════════════════════════════
            MODAL CONFIRM DELETE FORMAÇÃO
        ═══════════════════════════════════════ */}
        <Modal show={showDeleteTrainingModal} onHide={closeDeleteTrainingModal} centered backdrop="static" keyboard={!deleteTrainingLoading} className="pretty-modal">
          <Modal.Header closeButton={!deleteTrainingLoading}>
            <div className="header-row" style={{ width:"100%" }}>
              <div className="header-left">
                <div className="avatar-wrap" style={{ background:"rgba(239,68,68,.10)",borderColor:"rgba(239,68,68,.18)" }}>
                  <i className="bi bi-trash3" style={{ color:"#ef4444",fontSize:18 }} />
                </div>
                <div><div style={{ fontWeight:650 }}>Remover formação</div><div className="title-sub">Apaga a formação e as imagens.</div></div>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body>
            <div className="body-scroll">
              {deleteTrainingError && <Alert variant="danger" style={{ borderRadius:14 }}>{deleteTrainingError}</Alert>}
              <div className="cardlet">
                <div className="cardlet-title"><i className="bi bi-mortarboard" />Formação</div>
                <div style={{ fontWeight:650 }}>{safeValue(deleteTrainingTarget?.institute_name,"—")}</div>
                <div className="helper">
                  {safeValue(deleteTrainingTarget?.tr_institute_code,"—")} · {safeValue(deleteTrainingTarget?.tr_date,"—")} · {safeValue(deleteTrainingTarget?.tr_status,"—")}
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <div className="actions" style={{ width:"100%" }}>
              <Button variant="outline-secondary" className="btn-soft" onClick={closeDeleteTrainingModal} disabled={deleteTrainingLoading}>Cancelar</Button>
              <Button variant="danger" onClick={confirmDeleteTraining} disabled={deleteTrainingLoading}>
                {deleteTrainingLoading ? <><Spinner animation="border" size="sm" className="me-2" />A remover...</> : "Sim, remover"}
              </Button>
            </div>
          </Modal.Footer>
        </Modal>

      </div>
    </>
  );
}